// InviteModal — landlord copies the invite URL to share with a tenant.
import { useState, useEffect, useCallback, useMemo } from "react";
import api from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Copy, Check, X, Send } from "lucide-react";
import { toast } from "sonner";

export default function InviteModal({ open, onOpenChange, tenant, onUpdated }) {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(null);

  const load = useCallback(async () => {
    if (!tenant?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/manage/tenants/${tenant.id}/invites`);
      setInvites(data);
    } catch {
      toast.error("Could not load invites");
    } finally {
      setLoading(false);
    }
  }, [tenant?.id]);

  useEffect(() => { if (open) load(); }, [open, load]);

  // Derived lists — memoized so we don't filter the array on every render.
  const pending = useMemo(
    () => invites.find((i) => i.status === "pending"),
    [invites]
  );
  const history = useMemo(
    () => invites.filter((i) => i.status !== "pending"),
    [invites]
  );

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/manage/tenants/${tenant.id}/invite`);
      toast.success("Invite link created");
      setInvites([{ id: data.invite_id, token: data.token, expires_at: data.expires_at, status: "pending", accept_url: data.accept_url }, ...invites]);
      // Copy immediately for convenience
      try {
        await navigator.clipboard.writeText(data.accept_url);
        setCopied(data.invite_id);
        setTimeout(() => setCopied(null), 2500);
      } catch (err) {
        // Some browsers gate navigator.clipboard behind a user gesture; ignore but log in dev.
        if (process.env.NODE_ENV !== "production") {
          console.warn("InviteModal: clipboard.writeText failed (auto-copy on generate)", err);
        }
      }
      onUpdated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create invite");
    } finally {
      setLoading(false);
    }
  };

  const buildUrl = (token) => `${window.location.origin}/tenant/accept?token=${token}`;

  const copy = async (id, token) => {
    try {
      await navigator.clipboard.writeText(buildUrl(token));
      setCopied(id);
      setTimeout(() => setCopied(null), 2500);
      toast.success("Copied");
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.warn("InviteModal.copy clipboard failed", err);
      }
      toast.error("Could not copy — long-press the URL manually");
    }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this invite? The link will stop working.")) return;
    try {
      await api.post(`/manage/tenants/${tenant.id}/invite/${id}/revoke`);
      toast.success("Revoked");
      load();
      onUpdated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not revoke");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl border-slate-200 bg-white" data-testid="invite-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="h-4 w-4 text-indigo-600" /> Tenant portal invite
          </DialogTitle>
          <DialogDescription className="text-slate-500">
            Share the link below with <span className="font-medium">{tenant?.name}</span> ({tenant?.email}).
            They'll set their own password. Links expire in 30 days.
          </DialogDescription>
        </DialogHeader>

        {!pending && (
          <Button
            onClick={generate}
            className="w-full bg-indigo-600 text-white hover:bg-indigo-700"
            disabled={loading}
            data-testid="invite-generate"
          >
            <Send className="mr-2 h-4 w-4" /> Generate invite link
          </Button>
        )}

        {pending && (
          <div className="rounded-xl border border-indigo-200 bg-indigo-50/40 p-4" data-testid="invite-active">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-indigo-700">
              Pending invite — expires {new Date(pending.expires_at).toLocaleDateString()}
            </p>
            <div className="mt-2 flex items-stretch gap-2">
              <input
                readOnly
                value={buildUrl(pending.token)}
                className="flex-1 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700"
                data-testid="invite-url-field"
                onFocus={(e) => e.target.select()}
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => copy(pending.id, pending.token)}
                className="border-slate-200"
                data-testid="invite-copy"
              >
                {copied === pending.id ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
            <div className="mt-3 flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => revoke(pending.id)}
                className="border-red-200 text-red-700 hover:bg-red-50"
                data-testid="invite-revoke"
              >
                <X className="mr-1 h-3 w-3" /> Revoke
              </Button>
            </div>
          </div>
        )}

        {history.length > 0 && (
          <div className="mt-2">
            <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">History</p>
            <ul className="space-y-1.5">
              {history.map((i) => (
                <li key={i.id} className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs">
                  <span className="text-slate-700">
                    <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider ${
                      i.status === "accepted" ? "bg-emerald-100 text-emerald-700"
                      : i.status === "revoked" ? "bg-red-100 text-red-700"
                      : "bg-slate-200 text-slate-600"
                    }`}>{i.status}</span>
                    <span className="ml-2 text-slate-500">{new Date(i.created_at).toLocaleDateString()}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
